import { apiClient } from './apiClient';

export interface LoginRequest {
  username: string;
  password: string;
  rememberMe: boolean;
}

export interface UserProfile {
  userId: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  lastLoginDate?: string;
  passwordExpiryDate?: string;
  // Program-specific role information (populated for non-SuperAdmin users)
  programId?: string;
  programCode?: string;
  programName?: string;
  programRoleName?: string;
  roleCategory?: string;
  hospitalId?: string;
  hcpId?: string;
  patientId?: string;
  entityType?: string;
  dashboardKey?: string;
  landingRoute?: string;
  accessibleMenuKeys?: string[];
  permissionKeys?: string[];
  themePreference?: 'light' | 'dark';
}

export interface LoginResponse {
  token?: string;
  refreshToken?: string;
  expiresAt?: string;
  user?: UserProfile;
  mustChangePassword?: boolean;
  // MFA challenge fields (populated when requiresMfa = true)
  requiresMfa?: boolean;
  mfaToken?: string;
  maskedContact?: string;
  mfaChannel?: string;
  otpExpirySeconds?: number;
  // Login OTP challenge fields (populated when requiresOtp = true)
  requiresOtp?: boolean;
  challengeId?: string;
  maskedEmail?: string;
  expiresInSeconds?: number;
  programRoleName?: string;
  dashboardKey?: string;
  landingRoute?: string;
  accessibleMenuKeys?: string[];
  permissions?: string[];
  patientId?: string;
  programId?: string;
  redirectUrl?: string;
  status?: string;
  code?: string;
  approvalStatus?: string;
  patientStatus?: string;
  patientName?: string;
}

export interface SendOtpResponse {
  sent: boolean;
  maskedContact: string;
  channel: string;
  expirySeconds: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  message: string;
  errors: string[];
  correlationId: string;
}

class AuthService {
  private readonly TOKEN_KEY = 'epprem_token';
  private readonly REFRESH_TOKEN_KEY = 'epprem_refresh_token';
  private readonly USER_KEY = 'epprem_user';
  private readonly AUTH_TIMESTAMP_KEY = 'epprem_auth_timestamp';

  async login(username: string, password: string, rememberMe: boolean): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', {
        username,
        password,
        rememberMe,
      });

      // Only store auth data if fully authenticated (no MFA/OTP challenge pending)
      if (response.data.success && response.data.data && !response.data.data.requiresMfa && !response.data.data.requiresOtp) {
        this.setAuthData(response.data.data as Required<Pick<LoginResponse, 'token' | 'refreshToken' | 'user'>> & LoginResponse, rememberMe);
      }

      return response.data;
    } catch (error: unknown) {
      return this.extractApiError(error);
    }
  }

  async verifyMfa(mfaToken: string, otpCode: string, rememberMe: boolean): Promise<ApiResponse<LoginResponse>> {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/verify-mfa', {
      mfaToken,
      otpCode,
      rememberMe,
    });

    if (response.data.success && response.data.data && !response.data.data.requiresMfa) {
      this.setAuthData(response.data.data as Required<Pick<LoginResponse, 'token' | 'refreshToken' | 'user'>> & LoginResponse, rememberMe);
    }

    return response.data;
  }

  async sendOtp(identifier: string, programCode?: string): Promise<ApiResponse<SendOtpResponse>> {
    const response = await apiClient.post<ApiResponse<SendOtpResponse>>('/auth/send-otp', {
      identifier,
      programCode,
    });
    return response.data;
  }

  async otpLogin(identifier: string, otpCode: string, programCode?: string, rememberMe = false): Promise<ApiResponse<LoginResponse>> {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/otp-login', {
      identifier,
      otpCode,
      programCode,
      rememberMe,
    });

    if (response.data.success && response.data.data && !response.data.data.requiresMfa) {
      this.setAuthData(response.data.data as Required<Pick<LoginResponse, 'token' | 'refreshToken' | 'user'>> & LoginResponse, rememberMe);
    }

    return response.data;
  }

  async verifyLoginOtp(challengeId: string, otp: string): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/verify-login-otp', {
        challengeId,
        otp,
      });

      if (response.data.success && response.data.data?.token) {
        this.setAuthData(response.data.data as Required<Pick<LoginResponse, 'token' | 'refreshToken' | 'user'>> & LoginResponse, false);
      }

      return response.data;
    } catch (error: unknown) {
      return this.extractApiError(error);
    }
  }

  async hcpApprovalLogin(programId: string, patientId: string, approvalToken: string): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/hcp-approval-login', {
        programId,
        patientId,
        approvalToken,
      });

      if (response.data.success && response.data.data?.token && response.data.data?.refreshToken && response.data.data?.user) {
        this.setAuthData(
          response.data.data as Required<Pick<LoginResponse, 'token' | 'refreshToken' | 'user'>> & LoginResponse,
          false,
        );
      }

      return response.data;
    } catch (error: unknown) {
      return this.extractApiError(error);
    }
  }

  async resendLoginOtp(challengeId: string): Promise<ApiResponse<{ challengeId: string; maskedEmail: string; expiresInSeconds: number }>> {
    try {
      const response = await apiClient.post<ApiResponse<{ challengeId: string; maskedEmail: string; expiresInSeconds: number }>>('/auth/resend-login-otp', {
        challengeId,
      });
      return response.data;
    } catch (error: unknown) {
      return this.extractApiError(error);
    }
  }

  async logout(): Promise<void> {
    try {
      // Call server-side logout to invalidate any server session
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Continue with client-side cleanup even if server call fails
      console.warn('Server logout failed, continuing with client cleanup');
    } finally {
      // Always clear all client-side authentication data
      this.clearAllAuthData();
    }
  }

  private clearAllAuthData(): void {
    // Clear localStorage
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.AUTH_TIMESTAMP_KEY);

    // Clear sessionStorage
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.AUTH_TIMESTAMP_KEY);

    // Clear any additional app-specific storage
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('epprem_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('epprem_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }

  private extractApiError<T>(error: unknown): ApiResponse<T> {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: unknown } };
      const apiResponse = axiosError.response?.data;
      if (apiResponse && typeof apiResponse === 'object' && 'code' in apiResponse) {
        const structured = apiResponse as {
          code?: string;
          message?: string;
          approvalStatus?: string;
          patientStatus?: string;
          patientId?: string;
          programId?: string;
          patientName?: string;
        };

        if (structured.code) {
          return {
            success: false,
            data: {
              code: structured.code,
              status: structured.code,
              approvalStatus: structured.approvalStatus,
              patientStatus: structured.patientStatus,
              patientId: structured.patientId,
              programId: structured.programId,
              patientName: structured.patientName,
            } as T,
            message: structured.message || 'Unable to process approval link.',
            errors: [structured.code],
            correlationId: '',
          };
        }
      }

      if (apiResponse && typeof apiResponse === 'object' && 'success' in apiResponse) {
        const typedApiResponse = apiResponse as ApiResponse<T>;
        // Log correlationId for debugging
        if (typedApiResponse.correlationId) {
          console.error(`[Login Error] correlationId: ${typedApiResponse.correlationId}`);
        }
        return typedApiResponse;
      }

    }
    return {
      success: false,
      data: null,
      message: 'An unexpected error occurred. Please try again.',
      errors: ['An unexpected error occurred. Please try again.'],
      correlationId: '',
    };
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY) || sessionStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY) || sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  getCurrentUser(): UserProfile | null {
    const localUserJson = localStorage.getItem(this.USER_KEY);
    if (localUserJson) {
      try {
        return JSON.parse(localUserJson) as UserProfile;
      } catch {
        this.clearStoredUserData(localStorage);
      }
    }

    const sessionUserJson = sessionStorage.getItem(this.USER_KEY);
    if (sessionUserJson) {
      try {
        return JSON.parse(sessionUserJson) as UserProfile;
      } catch {
        this.clearStoredUserData(sessionStorage);
      }
    }

    return null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Check if token is expired
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      return Date.now() < expiryTime;
    } catch {
      return false;
    }
  }

  private clearStoredUserData(storage: Storage): void {
    storage.removeItem(this.TOKEN_KEY);
    storage.removeItem(this.REFRESH_TOKEN_KEY);
    storage.removeItem(this.USER_KEY);
    storage.removeItem(this.AUTH_TIMESTAMP_KEY);
  }

  private setAuthData(data: { token: string; refreshToken: string; user: UserProfile; expiresAt?: string; mustChangePassword?: boolean }, rememberMe: boolean): void {
    const storage = rememberMe ? localStorage : sessionStorage;
    
    storage.setItem(this.TOKEN_KEY, data.token);
    storage.setItem(this.REFRESH_TOKEN_KEY, data.refreshToken);
    storage.setItem(this.USER_KEY, JSON.stringify(data.user));
    storage.setItem(this.AUTH_TIMESTAMP_KEY, Date.now().toString());
  }

  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<ApiResponse<boolean>> {
    const response = await apiClient.post<ApiResponse<boolean>>('/auth/change-password', {
      currentPassword,
      newPassword,
      confirmPassword,
    });

    return response.data;
  }

  async forgotPassword(username: string): Promise<ApiResponse<ForgotPasswordResponse>> {
    const response = await apiClient.post<ApiResponse<ForgotPasswordResponse>>('/auth/forgot-password', {
      username,
    });

    return response.data;
  }

  async validateResetToken(token: string): Promise<ApiResponse<ValidateResetTokenResponse>> {
    const response = await apiClient.get<ApiResponse<ValidateResetTokenResponse>>('/auth/validate-reset-token', {
      params: { token },
    });

    return response.data;
  }

  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<ApiResponse<ResetPasswordResponse>> {
    const response = await apiClient.post<ApiResponse<ResetPasswordResponse>>('/auth/reset-password', {
      token,
      newPassword,
      confirmPassword,
    });

    return response.data;
  }

  async updateThemePreference(userId: string, theme: 'light' | 'dark'): Promise<ApiResponse<boolean>> {
    const response = await apiClient.put<ApiResponse<boolean>>('/auth/user-theme', {
      userId,
      themePreference: theme,
    });

    // Update stored user profile so preference persists across refreshes
    if (response.data.success) {
      const storedUser = this.getCurrentUser();
      if (storedUser) {
        storedUser.themePreference = theme;
        const usesLocal = !!localStorage.getItem(this.USER_KEY);
        const storage = usesLocal ? localStorage : sessionStorage;
        storage.setItem(this.USER_KEY, JSON.stringify(storedUser));
      }
    }

    return response.data;
  }
}

export interface ForgotPasswordResponse {
  emailSent: boolean;
  message: string;
}

export interface ValidateResetTokenResponse {
  isValid: boolean;
  email?: string;
  expiresAt?: string;
  message: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

export const authService = new AuthService();
